import React, { useState } from "react";
import { ArrowUpRight, Expand, Sparkles, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { useSiteContent } from "@/lib/site-content";

const FULL_MENU_IMAGE =
  "https://customer-assets.emergentagent.com/job_authentic-thai-taste/artifacts/51yv7mh5_IMG_3244.jpeg";

function DishCard({ dish, className = "" }) {
  const showImage = !!dish.image;
  return (
    <article
      data-testid={`menu-card-${dish.id}`}
      className={`lift relative rounded-3xl overflow-hidden border border-[#E8DED5] bg-white ${className}`}
    >
      {showImage && (
        <div className="relative h-48 md:h-64 overflow-hidden">
          <img
            src={dish.image}
            alt={dish.name}
            className="h-full w-full object-cover transition-transform duration-[1200ms] hover:scale-110"
          />
          {dish.tag && (
            <div className="absolute top-4 left-4 chip bg-white/95">
              {dish.tag}
            </div>
          )}
        </div>
      )}
      <div className="p-6 md:p-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            {dish.th && (
              <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#5C534D]">
                {dish.th}
              </div>
            )}
            <h3 className="font-display font-black text-2xl mt-1 leading-tight">
              {dish.name}
            </h3>
          </div>
          <div className="font-display font-black text-xl text-[#E53935] shrink-0">
            {dish.price}
          </div>
        </div>
        <p className="mt-3 text-[15px] text-[#5C534D] leading-relaxed">
          {dish.desc}
        </p>
      </div>
    </article>
  );
}

export default function MenuShowcase() {
  const { content } = useSiteContent();
  const menu = content.menu;
  const phoneHref = `tel:${(content.contact.phone || "").replace(/\s/g, "")}`;
  const fullMenuImage = content.full_menu_image || DEFAULT_CONTENT.full_menu_image;
  const [openMenu, setOpenMenu] = useState(false);

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="py-20 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <span className="chip" data-testid="menu-chip">
              <Sparkles className="h-3.5 w-3.5" />
              The menu
            </span>
            <h2 className="mt-5 font-display font-black tracking-tighter text-4xl md:text-5xl lg:text-6xl max-w-2xl">
              Street classics, <span className="italic font-light">plated</span>{" "}
              with <span className="text-[#E53935]">soul</span>.
            </h2>
          </div>
          <p className="max-w-sm text-[15px] text-[#5C534D] leading-relaxed">
            A tight, focused list of the dishes Thailand actually eats —
            reimagined for Manchester with British-sourced produce.
            <span className="block mt-3 text-xs italic">
              If you have a food allergy or special dietary requirement, please
              inform a member of our staff before you place your order.
            </span>
          </p>
        </div>

        {menu.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {menu.map((dish) => (
              <DishCard key={dish.id} dish={dish} />
            ))}
            <a
              href={phoneHref}
              data-testid="menu-cta-full"
              className="lift rounded-3xl bg-[#1A1A1A] text-white p-8 md:p-10 flex flex-col justify-between group min-h-[240px]"
            >
              <div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-[#F5A623] font-bold">
                  Full list
                </div>
                <div className="mt-3 font-display font-black text-3xl md:text-4xl leading-none">
                  40+ dishes — all wok-fresh
                </div>
              </div>
              <div className="mt-8 inline-flex items-center gap-2 font-semibold text-[#F5A623]">
                Call to enquire about specials
                <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </div>
            </a>
          </div>
        )}

        {/* Full printed menu showcase */}
        <div className="mt-16 md:mt-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <span className="chip">The full menu</span>
              <h3 className="mt-4 font-display font-black tracking-tighter text-3xl md:text-4xl max-w-2xl">
                Every dish, every <span className="text-[#E53935]">price</span>{" "}
                — tap to zoom.
              </h3>
            </div>
            <p className="text-sm text-[#5C534D] max-w-sm">
              Our complete in-restaurant menu — snacks, noodles, rice, salads,
              sides, drinks & dessert.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setOpenMenu(true)}
            data-testid="menu-full-open"
            aria-label="Open full menu"
            className="group relative block w-full rounded-3xl overflow-hidden border border-[#E8DED5] bg-white lift cursor-zoom-in"
          >
            <img
              src={fullMenuImage}
              alt="Thai Noodle & Rice — full printed menu"
              className="w-full h-auto object-contain transition-transform duration-700 group-hover:scale-[1.02]"
            />
            <div className="absolute top-4 right-4 h-11 w-11 rounded-full bg-white/95 backdrop-blur grid place-items-center shadow-md opacity-90 group-hover:opacity-100">
              <Expand className="h-5 w-5 text-[#1A1A1A]" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7 bg-gradient-to-t from-black/80 via-black/30 to-transparent">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#F5A623] font-bold">
                Our printed menu
              </div>
              <div className="mt-1 text-white font-display font-bold text-lg md:text-xl">
                Click to view full-size
              </div>
            </div>
          </button>
        </div>
      </div>

      <Dialog open={openMenu} onOpenChange={setOpenMenu}>
        <DialogContent
          data-testid="menu-full-dialog"
          className="max-w-[96vw] md:max-w-6xl p-0 bg-black border-0 overflow-hidden rounded-2xl [&>button]:hidden"
        >
          <div className="relative">
            <img
              src={fullMenuImage}
              alt="Thai Noodle & Rice — full printed menu"
              className="w-full max-h-[92vh] object-contain bg-black"
            />
            <button
              type="button"
              onClick={() => setOpenMenu(false)}
              aria-label="Close"
              data-testid="menu-full-close"
              className="absolute top-4 right-4 h-11 w-11 rounded-full bg-white/95 hover:bg-white grid place-items-center shadow-md"
            >
              <X className="h-5 w-5 text-[#1A1A1A]" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
